import { useEffect, useState } from 'react';
import { Box, CircularProgress, IconButton, Paper, Stack, Tooltip, Typography } from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { useAuth } from '../../../context/AuthContext';
import { DashboardService } from '../../../services/dashboard.service';
import { getWidget, isWidgetVisible, ModuleKey } from '../registry';
import { isGatedModule } from '../registry/moduleMeta';
import { CURRENCY_SYMBOL, fmtMoney } from '../registry/format';
import { compactMoney, formatPeriodLabel } from '../charts/axisPadding';
import StatTile, { StatTone } from '../charts/StatTile';
import { useDashboardAccess } from '../useDashboardAccess';
import { PERMISSIONS } from '../../../constants/permissions';
import { PresetKey } from '../registry/presets';
import { buildWorkTiles, isRolePreset, PRESET_TILE_ORDER } from './workSummary';
import { KPI_HELP } from './helpKeys';
import { tileLinkFor } from './tileLinks';
import { useModuleVisibility } from '../../modules/useModuleVisibility';
import { useKpiStripPrefs } from './useKpiStripPrefs';
import KpiStripEditDialog from './KpiStripEditDialog';

/**
 * Headline KPI strip shown at the top of the home board and of every module board: one outlined
 * Paper holding a wrapping row of StatTiles (same look as the Merchandising dashboard).
 *
 * Every tile comes from a DashboardService endpoint that the matching board widgets already use,
 * and a module's tiles are only requested when the caller could also see that module's own
 * summary widget (same permission + enabled-module gate the boards apply), so the strip never
 * causes a 403 the board would not. A tile whose call fails is simply omitted.
 */

export interface KpiTile {
    key: string;
    label: string;
    value: string | number;
    unit?: string;
    sub?: string;
    tone?: StatTone;
    /** Help-mode key; when absent the strip looks it up by `key` in KPI_HELP. */
    helpKey?: string;
}

interface Built { tile: KpiTile; home?: boolean }

/** Which registered widget's visibility decides whether the module's numbers may be requested. */
const GATE_WIDGET: Partial<Record<ModuleKey, string>> = {
    attendance: 'attendance.dayPulse',
    leave: 'leave.summary',
    people: 'people.summary',
    accounting: 'accounting.summary',
    sales: 'sales.summary',
    inventory: 'inventory.summary',
    payroll: 'payroll.summary',
    expense: 'expense.summary',
};

/** Home board: tile keys in display order; only tiles flagged `home` by their module are kept. */
const HOME_ORDER = [
    'att.present', 'att.absent', 'acc.cash', 'acc.ar', 'acc.ap', 'sales.mtd', 'pay.net',
    'exp.pending', 'leave.pending', 'people.active', 'sales.pipeline', 'inv.low',
];
const HOME_MAX = 10;

/**
 * The People board is the HR manager's workforce overview (attendance, leave and headcount
 * widgets), so its strip spans those three modules too; each module's tiles still only load when
 * the caller could see that module's own widgets.
 */
const PEOPLE_MODULES: ModuleKey[] = ['attendance', 'leave', 'people'];
const PEOPLE_ORDER = [
    'att.present', 'att.absent', 'att.leave', 'att.rate', 'leave.pending',
    'people.active', 'people.inactive', 'people.noDept', 'people.departments',
];

export const HOME_MODULES: ModuleKey[] = ['attendance', 'accounting', 'sales', 'payroll', 'expense', 'leave', 'people', 'inventory'];

type Loader = (institutionId: string, full: boolean, compact: boolean, extras: { attendance: boolean }) => Promise<Built[]>;

const settled = async <T,>(p: () => Promise<T>): Promise<T | null> => {
    try { return await p(); } catch { return null; }
};

const money = (n: number, compact: boolean) => (compact ? compactMoney(n) : fmtMoney(n));

const LOADERS: Partial<Record<ModuleKey, Loader>> = {
    attendance: async (id) => {
        const d = await DashboardService.getAttendanceSummary(id);
        const rate = d.totalUsers > 0 ? Math.round((d.presentToday / d.totalUsers) * 100) : null;
        const notStarted = Math.min(d.notStartedYetToday ?? 0, d.absentToday);
        const absent = d.absentToday - notStarted;
        const absentSub = [
            notStarted > 0 ? `${notStarted} not started yet` : '',
            d.onLeaveToday > 0 ? `${d.onLeaveToday} on leave` : '',
        ].filter(Boolean).join(' · ') || undefined;
        const out: Built[] = [
            { home: true, tile: { key: 'att.present', label: 'Present today', value: d.presentToday, sub: `of ${d.totalUsers}`, tone: 'good' } },
            { tile: { key: 'att.leave', label: 'On leave today', value: d.onLeaveToday } },
            // DA-15: people whose shift has not started yet are not absent, so they are taken out of the count and named.
            { home: true, tile: { key: 'att.absent', label: 'Absent today', value: absent, tone: absent > 0 ? 'warning' : 'default', sub: absentSub } },
        ];
        if (rate != null) out.push({ tile: { key: 'att.rate', label: 'Attendance today', value: `${rate}%`, sub: 'Present of all members' } });
        return out;
    },
    leave: async (id, full, _c, extras) => {
        const [d, att] = await Promise.all([
            DashboardService.getLeaveSummary(id),
            full && extras.attendance ? settled(() => DashboardService.getAttendanceSummary(id)) : Promise.resolve(null),
        ]);
        const out: Built[] = [
            { home: true, tile: { key: 'leave.pending', label: 'Pending leave approvals', value: d.pending, tone: d.pending > 0 ? 'warning' : 'default' } },
            { tile: { key: 'leave.approved', label: 'Approved', value: d.approved, tone: 'good', sub: 'All applications to date' } },
            { tile: { key: 'leave.rejected', label: 'Rejected', value: d.rejected, sub: 'All applications to date' } },
        ];
        if (att) out.push({ tile: { key: 'leave.today', label: 'On leave today', value: att.onLeaveToday } });
        return out;
    },
    people: async (id) => {
        const d = await DashboardService.getPeopleSummary(id);
        const departments = d.byDepartment.filter(b => b.label !== 'No department').length;
        return [
            { home: true, tile: { key: 'people.active', label: 'Active headcount', value: d.active, sub: `of ${d.total} members`, tone: 'good' } },
            { tile: { key: 'people.inactive', label: 'Inactive', value: d.inactive } },
            { tile: { key: 'people.noDept', label: 'No department', value: d.noDepartmentCount, tone: d.noDepartmentCount > 0 ? 'warning' : 'default', sub: 'Active members' } },
            { tile: { key: 'people.departments', label: 'Departments', value: departments, sub: 'With active members' } },
        ];
    },
    accounting: async (id, full, compact) => {
        const [d, aging, trend] = await Promise.all([
            DashboardService.getAccountingSummary(id),
            full ? settled(() => DashboardService.getAccountingArAging(id)) : Promise.resolve(null),
            full ? settled(() => DashboardService.getAccountingTrend(id)) : Promise.resolve(null),
        ]);
        const out: Built[] = [
            { home: true, tile: { key: 'acc.cash', label: 'Cash & bank', unit: CURRENCY_SYMBOL, value: money(d.cashBalance, compact), tone: d.cashBalance > 0 ? 'good' : d.cashBalance < 0 ? 'critical' : 'default' } },
            { home: true, tile: { key: 'acc.ar', label: 'Receivable', unit: CURRENCY_SYMBOL, value: money(d.arOutstanding, compact), sub: 'Unpaid sales invoices', tone: d.arOutstanding > 0 ? 'warning' : 'default' } },
            { home: true, tile: { key: 'acc.ap', label: 'Payable', unit: CURRENCY_SYMBOL, value: money(d.apOutstanding, compact), tone: d.apOutstanding > 0 ? 'warning' : 'default' } },
        ];
        if (aging && aging.length === 5) {
            const over = aging.slice(2);
            const amount = over.reduce((s, b) => s + b.amount, 0);
            const count = over.reduce((s, b) => s + b.invoiceCount, 0);
            out.push({ tile: { key: 'acc.overdue', label: 'Overdue > 30 days', unit: CURRENCY_SYMBOL, value: money(amount, compact), sub: `${count} invoice${count === 1 ? '' : 's'}`, tone: amount > 0 ? 'warning' : 'good' } });
        }
        if (trend && trend.length > 0) {
            const cur = trend[trend.length - 1];
            const net = cur.income - cur.expense;
            const month = formatPeriodLabel(cur.month);
            out.push(
                { tile: { key: 'acc.income', label: 'Income · this month', unit: CURRENCY_SYMBOL, value: money(cur.income, compact), sub: `${month} · posted journals` } },
                { tile: { key: 'acc.expense', label: 'Expense · this month', unit: CURRENCY_SYMBOL, value: money(cur.expense, compact), sub: month } },
                { tile: { key: 'acc.net', label: 'Net · this month', unit: CURRENCY_SYMBOL, value: money(net, compact), sub: 'Income minus expense', tone: net > 0 ? 'good' : net < 0 ? 'critical' : 'default' } },
            );
        }
        return out;
    },
    sales: async (id, full, compact) => {
        const [d, outcomes] = await Promise.all([
            DashboardService.getSalesSummary(id),
            full ? settled(() => DashboardService.getSalesOutcomes(id)) : Promise.resolve(null),
        ]);
        const out: Built[] = [
            { home: true, tile: { key: 'sales.mtd', label: 'Sales MTD', unit: CURRENCY_SYMBOL, value: money(d.mtdSalesTotal, compact), tone: d.mtdSalesTotal > 0 ? 'good' : 'default', sub: 'Invoiced this month' } },
            { home: true, tile: { key: 'sales.pipeline', label: 'Open pipeline', unit: CURRENCY_SYMBOL, value: money(d.pipelineValue, compact), sub: `${d.pipelineCount} open · CRM + quotes` } },
        ];
        if (full) out.push({ tile: { key: 'sales.deals', label: 'Open deals', value: d.pipelineCount, sub: 'CRM opportunities + quotes' } });
        if (outcomes) {
            const converted = outcomes.find(o => o.status === 'CONVERTED')?.count ?? 0;
            const closed = outcomes.reduce((s, o) => s + o.count, 0);
            if (closed > 0) {
                out.push({ tile: { key: 'sales.win', label: 'Quote win rate', value: `${Math.round((converted / closed) * 100)}%`, sub: `${converted} of ${closed} closed · 90 days` } });
            }
        }
        return out;
    },
    inventory: async (id, full, compact) => {
        const [d, stores] = await Promise.all([
            DashboardService.getInventorySummary(id),
            full ? settled(() => DashboardService.getInventoryValueByStore(id)) : Promise.resolve(null),
        ]);
        const out: Built[] = [
            { tile: { key: 'inv.value', label: 'Stock value', unit: CURRENCY_SYMBOL, value: money(d.stockValue, compact) } },
            { tile: { key: 'inv.items', label: 'Items', value: d.itemCount } },
            { home: true, tile: { key: 'inv.low', label: 'Low stock', value: d.lowStockCount, tone: d.lowStockCount > 0 ? 'warning' : 'default', sub: 'At or below reorder level' } },
        ];
        if (stores) out.push({ tile: { key: 'inv.stores', label: 'Stores with stock', value: stores.length } });
        return out;
    },
    payroll: async (id, full, compact) => {
        const [d, overtime] = await Promise.all([
            DashboardService.getPayrollSummary(id),
            full ? settled(() => DashboardService.getPayrollOvertime(id)) : Promise.resolve(null),
        ]);
        if (d.lastRunPeriod == null) return [];
        const out: Built[] = [
            { home: true, tile: { key: 'pay.net', label: 'Last payroll · net pay', unit: CURRENCY_SYMBOL, value: money(d.lastRunTotal, compact), sub: formatPeriodLabel(d.lastRunPeriod) } },
            { tile: { key: 'pay.headcount', label: 'Employees paid', value: d.headcount, sub: formatPeriodLabel(d.lastRunPeriod) } },
        ];
        if (d.headcount > 0) {
            out.push({ tile: { key: 'pay.avg', label: 'Avg net pay', unit: CURRENCY_SYMBOL, value: money(d.lastRunTotal / d.headcount, compact), sub: 'Per employee, last run' } });
        }
        if (overtime && overtime.length > 0) {
            const last = overtime[overtime.length - 1];
            out.push({ tile: { key: 'pay.ot', label: 'Overtime hours', value: Number.isInteger(last.hours) ? last.hours : last.hours.toFixed(1), sub: formatPeriodLabel(last.month) } });
        }
        return out;
    },
    expense: async (id, full, compact) => {
        const [d, queue] = await Promise.all([
            DashboardService.getExpenseSummary(id),
            full ? settled(() => DashboardService.getExpenseQueue(id)) : Promise.resolve(null),
        ]);
        const out: Built[] = [
            { home: true, tile: { key: 'exp.pending', label: 'Pending expense claims', value: d.pendingCount, sub: `${CURRENCY_SYMBOL}${fmtMoney(d.pendingAmount)}`, tone: d.pendingCount > 0 ? 'warning' : 'default' } },
            { tile: { key: 'exp.approved', label: 'Approved MTD', unit: CURRENCY_SYMBOL, value: money(d.mtdApprovedTotal, compact) } },
        ];
        if (full) out.push({ tile: { key: 'exp.amount', label: 'Awaiting approval', unit: CURRENCY_SYMBOL, value: money(d.pendingAmount, compact), sub: `${d.pendingCount} claim${d.pendingCount === 1 ? '' : 's'}`, tone: d.pendingAmount > 0 ? 'warning' : 'default' } });
        if (queue && queue.length > 0) {
            const oldest = Math.max(...queue.map(r => r.waitingDays));
            out.push({ tile: { key: 'exp.oldest', label: 'Longest waiting', value: `${oldest}d`, sub: 'Since last update', tone: oldest >= 7 ? 'warning' : 'default' } });
        }
        return out;
    },
};

/** Module whose loader supplies a tile key's prefix (role boards only). */
const PREFIX_MODULE: Record<string, ModuleKey> = {
    att: 'attendance', leave: 'leave', people: 'people', acc: 'accounting', sales: 'sales', pay: 'payroll', exp: 'expense',
};

interface Props {
    institutionId: string;
    /** Role board whose tiles to show; owner/basic/undefined keep the whole-business home strip. */
    preset?: PresetKey;
    /** `home` = cross-business headline tiles; a module key = that module's own KPIs. */
    scope: 'home' | ModuleKey;
}

export default function BoardKpiStrip({ institutionId, scope, preset }: Props) {
    const { hasPermission } = useAuth();
    const { enabledModules } = useDashboardAccess();
    const { visibility, enabledModules: navModules } = useModuleVisibility();

    const canSee = (m: ModuleKey): boolean => {
        const w = getWidget(GATE_WIDGET[m] ?? '');
        return !!w && (!isGatedModule(m) || enabledModules.has(m)) && isWidgetVisible(w, hasPermission);
    };

    const role = scope === 'home' && isRolePreset(preset) ? preset : null;
    const roleOrder = role ? PRESET_TILE_ORDER[role]! : [];
    const wantsWork = roleOrder.some(k => k.startsWith('ws.'));
    const wantsStore = roleOrder.some(k => k === 'req.pending' || k === 'inv.low');
    const canStore = enabledModules.has('inventory') && hasPermission(PERMISSIONS.DASHBOARD_INVENTORY_VIEW);
    const roleModules = Array.from(new Set(roleOrder.map(k => PREFIX_MODULE[k.split('.')[0]]).filter(Boolean)));

    const candidates = role ? roleModules : scope === 'home' ? HOME_MODULES : scope === 'people' ? PEOPLE_MODULES : [scope];
    const modules = candidates.filter(m => canSee(m) && LOADERS[m]);
    const attendanceOk = canSee('attendance');
    const modulesKey = modules.join(',');

    const [tiles, setTiles] = useState<KpiTile[] | null>(null);
    const prefs = useKpiStripPrefs(institutionId);
    const [editing, setEditing] = useState(false);

    useEffect(() => {
        let live = true;
        setTiles(null);
        const full = scope !== 'home';
        if (role) {
            const list = modulesKey ? (modulesKey.split(',') as ModuleKey[]) : [];
            Promise.all([
                Promise.allSettled(list.map(m => LOADERS[m]!(institutionId, false, true, { attendance: attendanceOk }))),
                wantsWork ? settled(() => DashboardService.getWorkSummary(institutionId)) : Promise.resolve(null),
                wantsStore && canStore ? settled(() => DashboardService.getStoreAdminDashboard(institutionId)) : Promise.resolve(null),
            ]).then(([results, ws, store]) => {
                if (!live) return;
                const all: KpiTile[] = [
                    ...results.flatMap(r => (r.status === 'fulfilled' ? r.value.map(b => b.tile) : [])),
                    ...buildWorkTiles(ws),
                ];
                if (store) {
                    all.push(
                        { key: 'req.pending', label: 'Requisitions awaiting approval', value: store.pendingApprovalRequisitions, tone: store.pendingApprovalRequisitions > 0 ? 'warning' : 'default' },
                        { key: 'inv.low', label: 'Low stock', value: store.lowStockItemCount, tone: store.lowStockItemCount > 0 ? 'warning' : 'default', sub: 'At or below reorder level' },
                    );
                }
                const seen = new Set<string>();
                setTiles(all.filter(t => roleOrder.includes(t.key) && !seen.has(t.key) && !!seen.add(t.key))
                    .sort((a, b) => roleOrder.indexOf(a.key) - roleOrder.indexOf(b.key))
                    .slice(0, HOME_MAX));
            });
            return () => { live = false; };
        }
        const list = modulesKey ? (modulesKey.split(',') as ModuleKey[]) : [];
        Promise.allSettled(list.map(m => LOADERS[m]!(institutionId, full && m === scope, !full, { attendance: attendanceOk }))).then(results => {
            if (!live) return;
            const built = results.flatMap(r => (r.status === 'fulfilled' ? r.value : []));
            let out: KpiTile[];
            if (scope === 'home') {
                out = built.filter(b => b.home)
                    .sort((a, b) => HOME_ORDER.indexOf(a.tile.key) - HOME_ORDER.indexOf(b.tile.key))
                    .slice(0, HOME_MAX).map(b => b.tile);
            } else if (scope === 'people') {
                out = built.map(b => b.tile).filter(t => PEOPLE_ORDER.includes(t.key))
                    .sort((a, b) => PEOPLE_ORDER.indexOf(a.key) - PEOPLE_ORDER.indexOf(b.key));
            } else {
                out = built.map(b => b.tile);
            }
            setTiles(out);
        });
        return () => { live = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [institutionId, scope, modulesKey, attendanceOk, role, canStore]);

    if (!role && modules.length === 0) return null;
    if (tiles !== null && tiles.length === 0) return null;

    const loading = tiles === null || !prefs.ready;
    const body = loading ? [] : tiles.filter(t => !prefs.hidden.has(t.key));

    return (
        <Paper variant="outlined" sx={{ p: 1, mb: 2, minWidth: 0, position: 'relative' }} data-testid="board-kpi-strip" data-help-section="dashboard.kpiStrip">
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}><CircularProgress size={20} /></Box>
            ) : (
                <>
                    {/* Straddles the top border, so the tiles keep the strip's full width at every screen size. */}
                    <Tooltip title="Edit tiles">
                        <span style={{ position: 'absolute', top: -10, right: 8, zIndex: 1 }}>
                            <IconButton size="small" aria-label="Edit tiles" disabled={prefs.saving} onClick={() => setEditing(true)}
                                data-help="dashboard.kpiStrip.edit"
                                sx={{ p: 0.25, bgcolor: 'background.paper', border: 1, borderColor: 'divider', '&:hover': { bgcolor: 'background.paper' } }}>
                                <EditOutlinedIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                        </span>
                    </Tooltip>
                    {body.length > 0 ? (
                        <Stack direction="row" useFlexGap sx={{ flexWrap: 'wrap' }}>
                            {body.map(t => (
                                <StatTile key={t.key} label={t.label} unit={t.unit} value={t.value} sub={t.sub} tone={t.tone} to={tileLinkFor(t.key, visibility, navModules)} help={t.helpKey ?? KPI_HELP[t.key]?.helpKey} />
                            ))}
                        </Stack>
                    ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ p: 1 }} data-testid="kpi-strip-empty">
                            All tiles are hidden. Use Edit tiles to bring them back.
                        </Typography>
                    )}
                </>
            )}
            {tiles !== null && (
                <KpiStripEditDialog
                    open={editing}
                    saving={prefs.saving}
                    tiles={tiles.map(t => ({ key: t.key, label: t.label }))}
                    hidden={prefs.hidden}
                    onClose={() => setEditing(false)}
                    onSave={prefs.save}
                />
            )}
        </Paper>
    );
}
